<script lang="ts">
  export default {
    inheritAttrs: false,
  }
</script>

<script setup lang="ts">
  import { computed } from 'vue'
  import SvgIcon from '@shared/components/svg-icon.vue'

  const props = defineProps<{
    size?: string | null // if medium, ignore
    outlined?: boolean
    accent?: boolean
    white?: boolean
    secondary?: boolean
    disabled?: boolean
    loading?: boolean
    isSso?: boolean
    isIcon?: boolean
    isRoundedIcon?: boolean
  }>()

  const btnClass = computed((): string => {
    if (props.isSso) {
      return 'is-sso'
    }
    if (props.isIcon) {
      return 'is-icon'
    }
    if (props.isRoundedIcon) {
      return 'is-rounded-icon'
    }
    if (props.accent) {
      return 'accent'
    }
    if (props.white) {
      return 'white'
    }
    return ''
  })
  const btnType = computed((): string => (!props.outlined ? 'primary' : ''))
</script>

<template>
  <el-button v-bind="$attrs" :class="btnClass" :size="size" :type="btnType" :disabled="disabled" :loading="loading">
    <template #loading>
      <i class="loading-icon is-loading">
        <svg-icon name="loading" />
      </i>
    </template>

    <template v-if="!loading">
      <slot name="icon" />
      <slot />
    </template>
  </el-button>
</template>

<style scoped lang="scss">
  .el-button {
    @apply tw-h-48 tw-font-sans tw-text-primary tw-text-button-2 tw-border-2 tw-border-solid tw-border-primary
      tw-rounded-24 tw-py-0 md:tw-py-12 tw-px-16 md:tw-px-32;

    & + .el-button {
      @apply tw-ml-0;
    }

    // States
    &:hover {
      @apply tw-text-primary tw-border-primary-100 tw-bg-transparent;
    }

    &:focus {
      @apply tw-text-primary tw-border-primary tw-bg-transparent tw-shadow-200;
    }

    &:active {
      @apply tw-text-primary-200 tw-border-primary-200 tw-bg-transparent;
    }

    // Type
    &--primary {
      @apply tw-text-grey-100 tw-bg-primary;

      // States
      &:hover {
        @apply tw-text-grey-100 tw-bg-primary-100;
      }

      &:focus {
        @apply tw-text-grey-100 tw-border-primary-200 tw-bg-primary-100;
      }

      &:active {
        @apply tw-text-grey-100 tw-border-primary-200 tw-bg-primary-200;
      }

      &.is-disabled {
        @apply tw-bg-grey-400;

        &:hover,
        &:focus,
        &:active {
          @apply tw-bg-grey-400;
        }
      }
    }

    // Size
    &--large {
      @apply tw-w-full tw-h-auto tw-py-16 tw-px-24 tw-text-button-1;
    }

    // Modificators
    &.is-text {
      @apply tw-bg-transparent tw-border-none;

      &:not(.is-disabled) {
        &:hover,
        &:focus {
          @apply tw-bg-transparent tw-shadow-none;
        }
      }
    }

    &.is-link {
      @apply tw-bg-transparent;

      &:focus {
        @apply tw-shadow-none;
      }
    }

    &.is-disabled {
      @apply tw-text-grey-500 tw-border-grey-400 tw-bg-transparent;

      &:hover,
      &:focus,
      &:active {
        @apply tw-text-grey-500 tw-border-grey-400 tw-bg-transparent;
      }
    }

    &.is-sso {
      @apply tw-py-12 tw-px-24 tw-text-grey-600 tw-text-subtitle-2;

      .icon {
        @apply tw-mr-10;
      }

      // States
      &:hover,
      &:focus {
        @apply tw-border-secondary-100 tw-bg-secondary-120 tw-text-primary-100;
      }

      &:active {
        @apply tw-border-secondary-100 tw-bg-secondary-100 tw-shadow-400;
      }

      &.is-disabled {
        @apply tw-text-grey-500 tw-bg-grey-400 tw-shadow-400;
      }
    }

    &.accent {
      @apply tw-h-48 tw-font-sans tw-text-white tw-text-button-2 tw-border-2 tw-border-solid tw-border-accent-500 tw-bg-accent-500
      tw-rounded-24 tw-py-0 md:tw-py-12 tw-px-16 md:tw-px-32;

      & + .el-button {
        @apply tw-ml-0;
      }

      // States

      &:focus {
        @apply tw-text-white tw-border-accent-500 tw-bg-accent-500 tw-shadow-200;
      }

      &:active {
        @apply tw-text-white tw-border-accent-550 tw-bg-accent-550;
      }

      &:hover {
        @apply tw-text-white tw-border-accent-550 tw-bg-accent-550;
      }
    }

    &.white {
      @apply tw-bg-white tw-text-primary-100;

      &:hover,
      &:focus,
      &:active {
        @apply tw-bg-grey tw-border-grey;
      }
    }

    &--small {
      @apply tw-h-[36px] tw-py-6 tw-px-16 tw-border-none tw-bg-secondary-100 tw-text-primary-100 tw-fill-primary-100;

      // States
      &:hover {
        @apply tw-text-primary-100 tw-bg-secondary-120;
      }

      &:focus {
        @apply tw-text-primary-100 tw-bg-secondary-120 tw-shadow-200;
      }

      &.is-disabled {
        @apply tw-text-grey-500 tw-fill-grey-500 tw-bg-grey-300;

        &:hover,
        &:focus,
        &:active {
          @apply tw-text-grey-500 tw-fill-grey-500 tw-bg-grey-300;
        }
      }

      &.is-icon {
        @apply tw-h-[40px] tw-w-[40px] tw-rounded-8;
      }

      &.is-rounded-icon {
        @apply tw-h-[32px] tw-w-[32px] tw-rounded-full;
      }
    }

    .loading-icon {
      animation: rotating 2s linear infinite;
    }
  }
</style>
